import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Save, ArrowLeft, Eye, Bold, Italic, List, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: '',
    category: '',
    tags: '',
    featured_image: '',
    published: false
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    const token = localStorage.getItem('admin_token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/blog/posts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const post = response.data;
      setFormData({
        ...post,
        tags: post.tags.join(', ')
      });
    } catch (error) {
      console.error('Error fetching post:', error);
      alert('Failed to load post');
      navigate('/admin/blog');
    }
    setLoading(false);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title: value,
      slug: isEditMode ? formData.slug : generateSlug(value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('admin_token');
    const postData = {
      ...formData,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      content: formData.content.replace(/\n/g, '<br>')
    };

    try {
      if (isEditMode) {
        await axios.put(`${API}/admin/blog/posts/${id}`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Post updated successfully!');
      } else {
        await axios.post(`${API}/admin/blog/posts`, postData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Post created successfully!');
      }
      navigate('/admin/blog');
    } catch (error) {
      console.error('Error saving post:', error);
      alert(error.response?.data?.detail || 'Failed to save post');
    }
    setSaving(false);
  };

  const insertFormatting = (before, after = '') => {
    const textarea = document.getElementById('content-editor');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const selectedText = text.substring(start, end);
    
    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setFormData({ ...formData, content: newText });
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/admin/blog')}
            className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span>Back to Blog</span>
          </button>
          <h1 className="text-4xl font-bold text-foreground" data-testid="editor-title">
            {isEditMode ? 'Edit Post' : 'Create New Post'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} data-testid="blog-editor-form">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl">
              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  data-testid="post-title"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full text-xl font-semibold"
                  placeholder="Enter post title..."
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Slug *
                </label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  data-testid="post-slug"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                  placeholder="post-slug-here"
                />
                <p className="text-xs text-muted-foreground mt-1">URL-friendly version of the title</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Excerpt *
                </label>
                <textarea
                  required
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  data-testid="post-excerpt"
                  rows={3}
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full resize-none"
                  placeholder="Brief description of the post..."
                ></textarea>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Content *
                </label>
                <div className="mb-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => insertFormatting('<strong>', '</strong>')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground"
                    title="Bold"
                  >
                    <Bold size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('<em>', '</em>')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground"
                    title="Italic"
                  >
                    <Italic size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('<h2>', '</h2>')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground text-xs font-bold"
                    title="Heading"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('<ul>\n<li>', '</li>\n</ul>')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground"
                    title="List"
                  >
                    <List size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('<a href="URL">', '</a>')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground"
                    title="Link"
                  >
                    <LinkIcon size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertFormatting('<img src="', '" alt="description" />')}
                    className="p-2 bg-muted border border-[#E2E8F0] hover:bg-slate-200 rounded-lg text-foreground"
                    title="Image"
                  >
                    <ImageIcon size={16} />
                  </button>
                </div>
                <textarea
                  id="content-editor"
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  data-testid="post-content"
                  rows={20}
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full resize-y font-mono text-sm"
                  placeholder="Write your blog post content here. You can use HTML tags for formatting..."
                ></textarea>
                <p className="text-xs text-muted-foreground mt-2">
                  Use HTML tags for formatting: &lt;strong&gt;bold&lt;/strong&gt;, &lt;em&gt;italic&lt;/em&gt;, &lt;h2&gt;heading&lt;/h2&gt;, etc.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl">
              <h3 className="text-foreground font-semibold mb-4">Publish</h3>
              
              <div className="mb-4">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                    data-testid="post-published"
                    className="w-5 h-5 rounded border-[#CBD5E1] bg-background text-blue-600 focus:ring-blue-600 focus:ring-offset-background"
                  />
                  <span className="text-muted-foreground">Publish immediately</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={saving}
                data-testid="save-post-button"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <Save size={18} />
                <span>{saving ? 'Saving...' : isEditMode ? 'Update Post' : 'Create Post'}</span>
              </button>

              {isEditMode && formData.published && (
                <a
                  href={`/blog/${formData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full bg-white border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-6 py-3 font-semibold transition-colors flex items-center justify-center space-x-2"
                >
                  <Eye size={18} />
                  <span>View Post</span>
                </a>
              )}
            </div>

            <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-6 rounded-2xl">
              <h3 className="text-foreground font-semibold mb-4">Post Details</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Author *
                </label>
                <input
                  type="text"
                  required
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  data-testid="post-author"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                  placeholder="Your name"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Category *
                </label>
                <input
                  type="text"
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  data-testid="post-category"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                  placeholder="Technology"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  data-testid="post-tags"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                  placeholder="React, Web Development"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Featured Image URL
                </label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  data-testid="post-featured-image"
                  className="bg-background border border-[#CBD5E1] rounded-lg px-4 py-2 text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none transition-all w-full"
                  placeholder="https://example.com/image.jpg"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
